/**
 * Admin tooling screen — internal-only. English UI (matches the CLI; not
 * customer-facing, so the i18n rule doesn't apply).
 *
 * Capabilities:
 *  - Search a user by email
 *  - View profile, learning progress, finished tests, streak, latest
 *    subscription, recent paid transactions, and the locked refund verdict
 *  - One-click "Refund this transaction" → POST /api/admin/refund
 *
 * Design intent: clean, info-dense, Linear/Stripe customer-detail style.
 * No emoji icons on data tiles, no chunky 3D shadows competing with the
 * consumer app. Looks intentional even when the user has zero activity.
 *
 * Auth: client-side hint via ADMIN_EMAILS allowlist (cosmetic). The Pages
 * Functions re-verify the Supabase JWT against the same allowlist on the
 * server (env ADMIN_EMAILS); a non-admin who guesses the URL gets a 403
 * panel instead of any data.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  Avatar,
  Button,
  Container,
  Dialog,
  Heading,
  Screen,
  Skeleton,
  Text,
  TextInput,
  useToast,
} from '@fluent/ui';
import { useAuth } from '../auth/authStore.js';
import { supabase } from '../lib/supabase.js';
import { useIsAdminEmail, DOMAIN_EXPIRY_FALLBACK } from '../admin/constants.js';
import { refundVerdict, type RefundVerdict } from '../admin/refundVerdict.js';
import {
  EXAM_MAX_SCORE,
  summarizeExams,
  type AdminExamSitting,
} from '../admin/examSummary.js';
import './Admin.css';

interface ProfileDto {
  id: string;
  email: string;
  display_name: string | null;
  locale: string;
  timezone: string;
  daily_goal_minutes: number;
  has_completed_onboarding: boolean;
  created_at: string;
}
interface ProgressDto {
  lesson_id: string;
  completed_at: string;
  accuracy: number;
  xp_earned: number;
  perfect: boolean;
}
interface StreakDto {
  current_streak: number;
  longest_streak: number;
  last_active_date: string | null;
  freezes_available: number;
}
interface SubscriptionDto {
  id: string;
  provider_subscription_id: string;
  provider_customer_id: string;
  status: 'trialing' | 'active' | 'past_due' | 'paused' | 'canceled';
  trial_ends_at: string | null;
  current_period_starts_at: string | null;
  current_period_ends_at: string | null;
  cancelled_at: string | null;
  last_event_type: string | null;
  last_event_at: string | null;
  created_at: string;
}
interface TransactionDto {
  id: string;
  status: string;
  billed_at: string | null;
  grand_total: string | null;
  currency: string | null;
}
interface UserStatsDto {
  found: true;
  admin: { email: string };
  profile: ProfileDto;
  /** Main-course lessons, newest first. */
  progress: ProgressDto[];
  /** Phrases-course (ფრაზები) lessons, newest first. */
  phraseProgress?: ProgressDto[];
  /** Every finished ტესტი sitting, newest first. */
  exams: AdminExamSitting[];
  xpTotal: number;
  streak: StreakDto | null;
  subscriptions: SubscriptionDto[];
  latestSub: SubscriptionDto | null;
  firstChargeAt: string | null;
  transactions: TransactionDto[];
}

interface AdminUserSummaryDto {
  id: string;
  email: string;
  display_name: string | null;
  locale: string;
  has_completed_onboarding: boolean;
  created_at: string;
  subscription_status: 'trialing' | 'active' | 'past_due' | 'paused' | 'canceled' | null;
  /** Pro given free (provider 'gift'), not paid for. */
  subscription_gift?: boolean;
  subscription_cancelled_at: string | null;
  subscription_period_ends_at: string | null;
  /** Main-course lessons. */
  lessons_completed: number;
  /** Phrases-course (ფრაზები) lessons. */
  phrase_lessons_completed?: number;
  /** Different tests finished (taking one test twice counts once). */
  tests_completed: number;
}
interface AdminStatsDto {
  users_total: number;
  paying_now: number;
  joined_today: number;
  joined_7d: number;
  active_today: number;
  active_7d: number;
  lessons_today: number;
  lessons_7d: number;
  funnel: {
    signed_up: number;
    started_lesson: number;
    finished_free_skill: number;
    ever_subscribed: number;
    paying_now: number;
  };
}

interface AdminUsersDto {
  admin: { email: string };
  count: number;
  paying: number;
  stats: AdminStatsDto;
  users: AdminUserSummaryDto[];
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string; status?: number }
  | { kind: 'not_found' }
  | { kind: 'ok'; data: UserStatsDto };

export type AdminListState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string; status?: number }
  | { kind: 'ok'; data: AdminUsersDto };
type ListState = AdminListState;

export function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [state, setState] = useState<FetchState>({ kind: 'idle' });
  const [confirming, setConfirming] = useState<TransactionDto | null>(null);
  const [refunding, setRefunding] = useState(false);
  const [listState, setListState] = useState<ListState>({ kind: 'idle' });
  const [listFilter, setListFilter] = useState('');

  const isAdmin = useIsAdminEmail(user?.email ?? null);
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setListState({ kind: 'loading' });
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess.session?.access_token;
        if (!token) {
          if (!cancelled) {
            setListState({ kind: 'error', message: 'No active session — sign in again.' });
          }
          return;
        }
        const r = await fetch('/api/admin/users', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) {
          const body = (await r.json().catch(() => ({}))) as { error?: string };
          if (!cancelled) {
            setListState({
              kind: 'error',
              message: body.error ?? `HTTP ${r.status}`,
              status: r.status,
            });
          }
          return;
        }
        const data = (await r.json()) as AdminUsersDto;
        if (!cancelled) setListState({ kind: 'ok', data });
      } catch (err) {
        if (!cancelled) {
          setListState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'list failed',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  if (user && !isAdmin) {
    return (
      <Screen>
        <Container size="md">
          <div className="admin-denied">
            <div className="admin-denied__icon">🔒</div>
            <Heading level={2}>Not authorized</Heading>
            <Text tone="muted">This page is for admins only.</Text>
            <Button variant="neutral" onClick={() => navigate('/')}>
              Back to home
            </Button>
          </div>
        </Container>
      </Screen>
    );
  }

  async function lookup(rawEmail: string) {
    const target = rawEmail.trim().toLowerCase();
    if (!target) return;
    setState({ kind: 'loading' });
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) {
        setState({ kind: 'error', message: 'No active session — sign in again.' });
        return;
      }
      const r = await fetch(`/api/admin/user-stats?email=${encodeURIComponent(target)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 404) {
        setState({ kind: 'not_found' });
        return;
      }
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        setState({
          kind: 'error',
          message: body.error ?? `HTTP ${r.status}`,
          status: r.status,
        });
        return;
      }
      const data = (await r.json()) as UserStatsDto;
      setState({ kind: 'ok', data });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'lookup failed',
      });
    }
  }

  async function issueRefund(txn: TransactionDto) {
    setRefunding(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) {
        toast({ tone: 'danger', title: 'No session', message: 'Sign in again.' });
        return;
      }
      const r = await fetch('/api/admin/refund', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transaction_id: txn.id,
          reason: 'Admin-issued refund (fluent.ge admin tool).',
        }),
      });
      const body = (await r.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        detail?: string;
      };
      if (!r.ok || !body.ok) {
        toast({
          tone: 'danger',
          title: 'Refund failed',
          message: body.error
            ? `${body.error}${body.detail ? ` — ${body.detail.slice(0, 160)}` : ''}`
            : `HTTP ${r.status}`,
        });
        return;
      }
      toast({
        tone: 'success',
        title: 'Refund issued',
        message: `Paddle returned OK for ${txn.id}.`,
      });
      setConfirming(null);
      if (state.kind === 'ok') {
        void lookup(state.data.profile.email);
      }
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Refund error',
        message: err instanceof Error ? err.message : 'unknown',
      });
    } finally {
      setRefunding(false);
    }
  }

  return (
    <Screen>
      <Container size="xl">
        <div className="admin-screen">
          <header className="admin-topbar">
            <h1 className="admin-topbar__title">Admin</h1>
            <span className="admin-topbar__signed-as">{user?.email ?? '—'}</span>
          </header>

          {/* The numbers first — the question is always "how are we doing",
              not "look up a user" (Tornike, 2026-09-23). */}
          <NumbersSection state={listState} />

          <form
            className="admin-search"
            onSubmit={(e) => {
              e.preventDefault();
              void lookup(email);
            }}
          >
            <div className="admin-search__field">
              <TextInput
                label="Find a person by email"
                type="email"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div className="admin-search__btn">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={state.kind === 'loading'}
              >
                Find
              </Button>
            </div>
          </form>

          {state.kind === 'loading' && <ResultSkeleton />}
          {state.kind === 'not_found' && (
            <div className="admin-error">No profile found for {email}.</div>
          )}
          {state.kind === 'error' && (
            <div className="admin-error">
              {state.status === 403
                ? 'Not authorized — your email is not in the admin allowlist.'
                : `Error: ${state.message}`}
            </div>
          )}
          {state.kind === 'ok' && (
            <AdminResult data={state.data} onRefund={(t) => setConfirming(t)} />
          )}

          <UsersListSection
            state={listState}
            filter={listFilter}
            onFilter={setListFilter}
            onPick={(e) => {
              setEmail(e);
              void lookup(e);
            }}
          />
          <DomainBanner />
        </div>
      </Container>

      <Dialog
        open={!!confirming}
        onOpenChange={(o) => !o && setConfirming(null)}
        title="Issue full refund?"
        description={
          confirming
            ? `This will issue a full refund for transaction ${confirming.id} via Paddle. The customer will receive ${confirming.grand_total ? formatPaddleAmount(confirming.grand_total, confirming.currency) : '?'} back on their card within 5–10 business days.`
            : ''
        }
        actions={
          <>
            <Button variant="neutral" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => confirming && void issueRefund(confirming)}
              loading={refunding}
            >
              Issue refund
            </Button>
          </>
        }
      />
    </Screen>
  );
}

/**
 * The headline numbers. Everything here answers a question Tornike asks
 * out loud: how many people are there, how many pay, is anyone learning
 * today, and where do the rest fall out.
 */
export function NumbersSection({ state }: { state: ListState }) {
  if (state.kind === 'loading' || state.kind === 'idle') {
    return (
      <div className="admin-numbers">
        <Skeleton height={92} />
        <Skeleton height={92} />
        <Skeleton height={92} />
        <Skeleton height={92} />
      </div>
    );
  }
  if (state.kind !== 'ok') return null;
  const s = state.data.stats;

  return (
    <>
      <div className="admin-numbers">
        <BigStat label="People" value={s.users_total} sub={`+${s.joined_today} today`} />
        <BigStat label="Paying" value={s.paying_now} sub={`${s.funnel.ever_subscribed} ever tried`} tone="money" />
        <BigStat label="Learning today" value={s.active_today} sub={`${s.active_7d} this week`} />
        <BigStat label="Lessons today" value={s.lessons_today} sub={`${s.lessons_7d} this week`} />
      </div>

      <div className="admin-card">
        <div className="admin-card__head">
          <h3 className="admin-card__title">Where people stop</h3>
        </div>
        <FunnelBars funnel={s.funnel} />
      </div>
    </>
  );
}

function BigStat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: number;
  sub?: string;
  tone?: 'money' | 'live';
}) {
  return (
    <div className="admin-bigstat" data-tone={tone}>
      <span className="admin-bigstat__value">{value}</span>
      <span className="admin-bigstat__label">{label}</span>
      {sub != null && <span className="admin-bigstat__sub">{sub}</span>}
    </div>
  );
}

/** Each step as a share of the one above it, so the drop-off is the shape. */
function FunnelBars({ funnel }: { funnel: AdminStatsDto['funnel'] }) {
  const steps: Array<{ label: string; value: number }> = [
    { label: 'Signed up', value: funnel.signed_up },
    { label: 'Started a lesson', value: funnel.started_lesson },
    { label: 'Finished the free skill', value: funnel.finished_free_skill },
    { label: 'Started a trial', value: funnel.ever_subscribed },
    { label: 'Paying now', value: funnel.paying_now },
  ];
  const top = Math.max(1, steps[0]!.value);
  return (
    <ol className="admin-funnel">
      {steps.map((step, i) => {
        const prev = i === 0 ? null : steps[i - 1]!.value;
        const share = Math.round((step.value / top) * 100);
        const kept = prev == null || prev === 0 ? null : Math.round((step.value / prev) * 100);
        return (
          <li key={step.label} className="admin-funnel__row">
            <span className="admin-funnel__label">{step.label}</span>
            <span className="admin-funnel__track" aria-hidden="true">
              <span className="admin-funnel__fill" style={{ width: `${Math.max(share, 2)}%` }} />
            </span>
            <span className="admin-funnel__value">
              {step.value}
              {kept != null && <span className="admin-funnel__kept">{kept}%</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function UsersListSection({
  state,
  filter,
  onFilter,
  onPick,
}: {
  state: ListState;
  filter: string;
  onFilter: (v: string) => void;
  onPick: (email: string) => void;
}) {
  const filtered = useMemo(() => {
    if (state.kind !== 'ok') return [];
    const q = filter.trim().toLowerCase();
    if (!q) return state.data.users;
    return state.data.users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.display_name ?? '').toLowerCase().includes(q),
    );
  }, [state, filter]);

  return (
    <div className="admin-card admin-users">
      <div className="admin-card__head">
        <h3 className="admin-card__title">People</h3>
        {state.kind === 'ok' && state.data.users.length > 0 && (
          <div className="admin-users__filter">
            <TextInput
              type="search"
              aria-label="Filter customers"
              placeholder="Filter by email or name"
              value={filter}
              onChange={(e) => onFilter(e.target.value)}
              autoComplete="off"
            />
          </div>
        )}
      </div>

      {state.kind === 'loading' && (
        <div className="admin-users__loading">
          <Skeleton height={36} />
          <Skeleton height={36} />
          <Skeleton height={36} />
        </div>
      )}
      {state.kind === 'error' && (
        <div className="admin-error">
          {state.status === 403
            ? 'Not authorized — your email is not in the admin allowlist.'
            : `Error: ${state.message}`}
        </div>
      )}
      {state.kind === 'ok' && state.data.users.length === 0 && (
        <div className="admin-empty-row">No customers yet.</div>
      )}
      {state.kind === 'ok' && state.data.users.length > 0 && (
        <div className="admin-users__table" role="table">
          <div className="admin-users__row admin-users__row--head" role="row">
            <span role="columnheader">Person</span>
            <span role="columnheader">Joined</span>
            <span role="columnheader">Lessons</span>
            <span role="columnheader">Tests</span>
            <span role="columnheader">Plan</span>
          </div>
          {filtered.map((u) => (
            <div
              key={u.id}
              role="row"
              tabIndex={0}
              className="admin-users__row admin-users__row--data"
              onClick={() => onPick(u.email)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPick(u.email);
                }
              }}
            >
              {/* Name over email: the name is what he recognises, the email is
                  what he searches. Locale and the onboarding flag are gone —
                  every row said "ka" and nobody ever read the flag. */}
              <span role="cell" className="admin-users__person">
                <span className="admin-users__name">{u.display_name ?? u.email.split('@')[0]}</span>
                <span className="admin-users__email">{u.email}</span>
              </span>
              <span role="cell" className="admin-users__when">
                {fmtDateRel(u.created_at)}
              </span>
              {/* Two columns on desktop (display: contents), one line on a phone. */}
              <span role="none" className="admin-users__counts">
                <span
                  role="cell"
                  className="admin-users__lessons"
                  data-empty={lessonsDone(u) === 0 || undefined}
                  data-one={lessonsDone(u) === 1 || undefined}
                >
                  {lessonsDone(u) > 0 ? lessonsDone(u) : 'no lessons'}
                </span>
                <span
                  role="cell"
                  className="admin-users__tests"
                  data-empty={u.tests_completed === 0 || undefined}
                  data-one={u.tests_completed === 1 || undefined}
                >
                  {u.tests_completed > 0 ? u.tests_completed : 'no tests'}
                </span>
              </span>
              <span role="cell" className="admin-users__sub">
                {u.subscription_status ? (
                  <span className="admin-status" data-status={u.subscription_status}>
                    {u.subscription_gift ? 'gift' : u.subscription_status}
                  </span>
                ) : (
                  <span className="admin-users__sub-none">free</span>
                )}
              </span>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="admin-empty-row">
              No customers match &ldquo;{filter}&rdquo;.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultSkeleton() {
  return (
    <div className="admin-stack">
      <Skeleton height={88} />
      <Skeleton height={104} />
      <Skeleton height={200} />
    </div>
  );
}

export function AdminResult({
  data,
  onRefund,
}: {
  data: UserStatsDto;
  onRefund: (t: TransactionDto) => void;
}) {
  const verdict = useMemo<RefundVerdict>(
    () =>
      refundVerdict({
        subscription: data.latestSub
          ? {
              status: data.latestSub.status,
              trial_ends_at: data.latestSub.trial_ends_at,
              current_period_starts_at: data.latestSub.current_period_starts_at,
            }
          : null,
        firstChargeAt: data.firstChargeAt,
      }),
    [data.latestSub, data.firstChargeAt],
  );

  const phraseProgress = data.phraseProgress ?? [];
  const totalLessons = data.progress.length + phraseProgress.length;
  const tests = useMemo(() => summarizeExams(data.exams), [data.exams]);
  // "Active" = the newest lesson of either course (both lists are newest first).
  const lastCompletion =
    [data.progress[0], phraseProgress[0]]
      .filter((row): row is ProgressDto => row !== undefined)
      .sort((a, b) => Date.parse(b.completed_at) - Date.parse(a.completed_at))[0] ?? null;
  const displayName = data.profile.display_name ?? data.profile.email.split('@')[0];

  return (
    <div className="admin-stack">
      {/* Compact identity strip */}
      <div className="admin-identity">
        <div className="admin-identity__avatar">
          <Avatar size={56} name={displayName} />
        </div>
        <div className="admin-identity__main">
          <h2 className="admin-identity__name">{displayName}</h2>
          <div className="admin-identity__email">{data.profile.email}</div>
          <div className="admin-identity__chips">
            <span className="admin-chip">
              {data.profile.locale === 'ka' ? 'ქართული' : 'English'}
            </span>
            <span className="admin-chip">
              Joined {fmtDateRel(data.profile.created_at)}
            </span>
            {lastCompletion && (
              <span className="admin-chip">
                Active {fmtDateRel(lastCompletion.completed_at)}
              </span>
            )}
            {!data.profile.has_completed_onboarding && (
              <span className="admin-chip" data-tone="warn">
                Onboarding incomplete
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stat strip */}
      <div className="admin-stats">
        <Stat
          value={totalLessons}
          label={phraseProgress.length ? `Lessons (${phraseProgress.length} phrases)` : 'Lessons'}
          empty={!totalLessons}
        />
        <Stat value={tests.length} label="Tests" empty={!tests.length} />
        <Stat value={data.xpTotal} label="XP" empty={!data.xpTotal} />
        <Stat
          value={data.streak?.current_streak ?? 0}
          label={
            data.streak?.longest_streak
              ? `Streak (best ${data.streak.longest_streak})`
              : 'Streak'
          }
          empty={!data.streak?.current_streak}
        />
      </div>

      {/* Tests: one line per test, best score first — "did they pass" is his
          call, the app has no pass mark. */}
      {tests.length > 0 && (
        <div className="admin-card">
          <div className="admin-card__head">
            <h3 className="admin-card__title">Tests ({tests.length})</h3>
          </div>
          <div className="admin-tests">
            {tests.map((t) => (
              <div key={t.test} className="admin-test">
                <span className="admin-test__name">Test {t.test}</span>
                <span className="admin-test__score">
                  {t.best}
                  <span className="admin-test__max">/{EXAM_MAX_SCORE}</span>
                </span>
                <span className="admin-test__meta">
                  {t.tries > 1 ? `best of ${t.tries} · ` : ''}
                  {fmtDateRel(t.lastAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscription — central card */}
      <div className="admin-card">
        <div className="admin-card__head">
          <h3 className="admin-card__title">Subscription</h3>
          {data.latestSub && (
            <span className="admin-status" data-status={data.latestSub.status}>
              {data.latestSub.status}
            </span>
          )}
        </div>
        {data.latestSub ? (
          <>
            {/* The three lines that answer a support question. Everything else
                is plumbing and hides behind the toggle (Tornike 2026-09-23:
                "remove the useless information"). */}
            <dl className="admin-kv">
              <KvRow
                label={data.latestSub.cancelled_at ? 'Access until' : 'Next charge'}
                value={fmtDate(data.latestSub.current_period_ends_at)}
              />
              <KvRow label="First paid" value={fmtDate(data.firstChargeAt)} />
              {data.latestSub.trial_ends_at && (
                <KvRow label="Trial ends" value={fmtDate(data.latestSub.trial_ends_at)} />
              )}
            </dl>
            <details className="admin-details">
              <summary>Technical details</summary>
              <dl className="admin-kv">
                <KvRow label="Provider sub id" value={data.latestSub.provider_subscription_id} />
                <KvRow
                  label="Period starts"
                  value={fmtDate(data.latestSub.current_period_starts_at)}
                />
                <KvRow label="Cancelled at" value={fmtDate(data.latestSub.cancelled_at)} />
                <KvRow
                  label="Last event"
                  value={`${data.latestSub.last_event_type ?? '—'} @ ${fmtDate(data.latestSub.last_event_at)}`}
                />
              </dl>
            </details>
          </>
        ) : (
          <div className="admin-empty-row">No subscription on file — this is a free user.</div>
        )}
      </div>

      {/* Refund verdict */}
      <div className="admin-verdict" data-tag={verdict.tag}>
        <div className="admin-verdict__icon" aria-hidden="true">
          {verdictIcon(verdict.tag)}
        </div>
        <div className="admin-verdict__body">
          <div className="admin-verdict__tag">{verdictHeadline(verdict.tag)}</div>
          <div className="admin-verdict__reason">{verdict.reason}</div>
        </div>
      </div>

      {/* Transactions */}
      {data.transactions.length > 0 && (
        <div className="admin-card">
          <div className="admin-card__head">
            <h3 className="admin-card__title">
              Recent paid transactions ({data.transactions.length})
            </h3>
          </div>
          <div className="admin-txns">
            {data.transactions.map((t) => (
              <div key={t.id} className="admin-txn">
                <div className="admin-txn__when">{fmtDate(t.billed_at)}</div>
                <div className="admin-txn__amount">
                  {t.grand_total ? formatPaddleAmount(t.grand_total, t.currency) : '—'}
                </div>
                <div className="admin-txn__id">{t.id}</div>
                <div className="admin-txn__action">
                  <Button size="sm" variant="danger" onClick={() => onRefund(t)}>
                    Refund
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <p className="admin-txn-foot">
            Refund button calls Paddle directly. The verdict above is advisory — Paddle is the
            source of truth for whether a transaction is still refundable.
          </p>
        </div>
      )}
    </div>
  );
}

/** Lessons done in both courses — a ფრაზები-only learner (B1+, past the
 *  main course's A2) is an active learner, not "no lessons". */
function lessonsDone(u: AdminUserSummaryDto): number {
  return u.lessons_completed + (u.phrase_lessons_completed ?? 0);
}

function Stat({
  value,
  label,
  empty,
}: {
  value: string | number;
  label: string;
  empty?: boolean;
}) {
  return (
    <div className="admin-stat" data-empty={empty || undefined}>
      <span className="admin-stat__value">{value}</span>
      <span className="admin-stat__label">{label}</span>
    </div>
  );
}

function KvRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function DomainBanner() {
  const expiresAt =
    (import.meta.env.VITE_DOMAIN_EXPIRES_AT as string | undefined) ?? DOMAIN_EXPIRY_FALLBACK;
  const daysLeft = useMemo(() => {
    const exp = new Date(`${expiresAt}T00:00:00Z`).getTime();
    if (Number.isNaN(exp)) return null;
    return Math.ceil((exp - Date.now()) / 86_400_000);
  }, [expiresAt]);

  if (daysLeft === null) return null;
  const tone = daysLeft <= 0 ? 'danger' : daysLeft <= 60 ? 'warn' : 'ok';
  const text =
    daysLeft <= 0
      ? `fluent.ge EXPIRED ${-daysLeft} days ago (${expiresAt}) — renew immediately at nic.ge`
      : daysLeft <= 60
        ? `fluent.ge expires in ${daysLeft} days (${expiresAt}) — renew at nic.ge`
        : `fluent.ge expires in ${daysLeft} days (${expiresAt})`;
  return (
    <div className="admin-domain-banner" data-tone={tone}>
      <span aria-hidden="true">{tone === 'ok' ? '🌐' : '⚠️'}</span>
      {text}
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
}

function fmtDateRel(iso: string): string {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function formatPaddleAmount(minor: string, currency: string | null): string {
  const n = Number(minor);
  if (!Number.isFinite(n)) return minor;
  const major = n / 100;
  return `${major.toFixed(2)} ${currency ?? ''}`.trim();
}

function verdictIcon(tag: RefundVerdict['tag']): string {
  switch (tag) {
    case 'eligible_48h':
      return '✓';
    case 'in_trial':
      return '⏱';
    case 'no_subscription':
    case 'never_charged':
      return '−';
    case 'not_eligible':
      return '✕';
  }
}

function verdictHeadline(tag: RefundVerdict['tag']): string {
  switch (tag) {
    case 'eligible_48h':
      return 'Eligible — 48-hour window';
    case 'in_trial':
      return 'In free trial';
    case 'no_subscription':
      return 'No subscription';
    case 'never_charged':
      return 'Never charged';
    case 'not_eligible':
      return 'Not eligible by default rules';
  }
}
